import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, photoOf } from "@/lib/orgUtils";
import { Camera, Linkedin, Sparkles, X } from "lucide-react";

const SS_FIELDS = [
  ["linkedin_url", "LinkedIn URL", "https://linkedin.com/in/you"],
  ["date_of_birth", "Date of Birth", "YYYY-MM-DD"],
  ["address", "Address", "Your home address"],
  ["highest_qualification", "Highest Qualification", "e.g. MBA, B.Tech"],
];

function Wizard({ emp, toggles, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const fileRef = useRef();
  const steps = [
    toggles.photo !== false && "photo",
    toggles.linkedin_url !== false && "linkedin",
    "details",
  ].filter(Boolean);

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/me/photo", fd);
      toast.success("Photo uploaded");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const finish = async () => {
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
    if (Object.keys(payload).length) {
      try { await api.put("/me/profile", payload); toast.success("Profile updated"); } catch (e) { toast.error(errMsg(e)); }
    }
    await api.post("/auth/complete-first-login").catch(() => {});
    onSaved();
    onClose();
  };

  const cur = steps[step];
  return (
    <div className="fixed inset-0 z-50 bg-[#181B34]/50 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-7" data-testid="enrichment-wizard">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#FDAB3D]" />
            <h3 className="text-lg font-black tracking-tight">Welcome, {emp?.full_name?.split(" ")[0]}!</h3>
          </div>
          <button data-testid="wizard-skip-btn" onClick={async () => { await api.post("/auth/complete-first-login").catch(() => {}); onClose(); }} className="text-xs font-bold text-[#9699AD] hover:text-[#181B34]">Complete later</button>
        </div>
        <div className="mt-3 flex gap-1.5">
          {steps.map((_, i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#6161FF]" : "bg-[#F0F3FF]"}`} />)}
        </div>
        <div className="mt-6 min-h-44">
          {cur === "photo" && (
            <div className="text-center">
              <p className="font-bold">Add your photo</p>
              <p className="text-sm text-[#676a82] font-medium mt-1">Put a face to your name on the org chart.</p>
              <button onClick={() => fileRef.current?.click()} className="mt-5 mx-auto w-24 h-24 rounded-full bg-[#F0F3FF] border-2 border-dashed border-[#6161FF]/40 flex items-center justify-center text-[#6161FF] hover:bg-[#6161FF]/10">
                {photoOf(emp) ? <img src={photoOf(emp)} alt="" className="w-full h-full rounded-full object-cover" /> : <Camera size={26} />}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
            </div>
          )}
          {cur === "linkedin" && (
            <div>
              <p className="font-bold flex items-center gap-2"><Linkedin size={16} className="text-[#579BFC]" /> Your LinkedIn</p>
              <p className="text-sm text-[#676a82] font-medium mt-1">Help colleagues connect with you.</p>
              <input data-testid="wizard-linkedin-input" value={form.linkedin_url || ""} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                placeholder="https://linkedin.com/in/you"
                className="mt-4 w-full h-11 px-3 rounded-xl border border-[#181B34]/15 font-semibold focus:outline-none focus:border-[#6161FF]" />
            </div>
          )}
          {cur === "details" && (
            <div className="space-y-3">
              <p className="font-bold">A few more details (all optional)</p>
              {SS_FIELDS.filter(([k]) => k !== "linkedin_url" && toggles[k] !== false).map(([k, label, ph]) => (
                <div key={k}>
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">{label}</label>
                  <input data-testid={`wizard-${k}-input`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-[#181B34]/15 text-sm font-semibold focus:outline-none focus:border-[#6161FF]" />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-between">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="px-4 h-10 rounded-xl font-bold text-[#4b4e63] disabled:opacity-30">← Back</button>
          {step < steps.length - 1 ? (
            <button data-testid="wizard-next-btn" onClick={() => setStep(step + 1)} className="px-6 h-10 rounded-xl bg-[#6161FF] text-white font-bold">Next</button>
          ) : (
            <button data-testid="wizard-finish-btn" onClick={finish} className="px-6 h-10 rounded-xl bg-[#00C875] text-white font-bold">Finish</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [emp, setEmp] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [showWizard, setShowWizard] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    const [e, s] = await Promise.all([api.get("/employees"), api.get("/settings")]);
    const mine = e.data.find((x) => x.id === user?.linked_employee_id);
    setEmp(mine || null);
    setSettings(s.data);
    if (mine) setForm({ linkedin_url: mine.linkedin_url || "", date_of_birth: mine.date_of_birth || "", address: mine.address || "", highest_qualification: mine.highest_qualification || "" });
  }, [user]);

  useEffect(() => {
    load();
    if (user?.first_login && user?.linked_employee_id) setShowWizard(true);
  }, [load, user]);

  const toggles = settings?.self_service || {};
  const completeness = emp ? Math.round(
    ([photoOf(emp), emp.linkedin_url, emp.date_of_birth, emp.address, emp.highest_qualification].filter(Boolean).length / 5) * 100
  ) : 0;

  const save = async () => {
    try {
      await api.put("/me/profile", form);
      toast.success("Profile saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/me/photo", fd);
      toast.success("Photo updated");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!user?.linked_employee_id) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 text-center p-8" data-testid="profile-no-link">
        <h2 className="text-2xl font-black tracking-tight">No employee record linked</h2>
        <p className="text-[#676a82] font-medium max-w-md">Your account ({user?.email}) isn't linked to an employee record. Platform accounts (admins) without a directory entry don't have a self-service profile.</p>
      </div>
    );
  }
  if (!emp) return <div className="h-full flex items-center justify-center"><div className="w-8 h-8 rounded-full border-4 border-[#6161FF] border-t-transparent animate-spin" /></div>;

  return (
    <div className="h-full overflow-auto p-8" data-testid="profile-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-[#181B34]/10 shadow-sm overflow-hidden">
          <div className="h-24 bg-[#6161FF] organa-grain" />
          <div className="px-7 pb-7">
            <div className="flex items-end gap-4 -mt-10">
              <div className="relative">
                {photoOf(emp) ? (
                  <img src={photoOf(emp)} alt="" className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg" />
                ) : (
                  <span className="w-24 h-24 rounded-2xl bg-[#F0F3FF] border-4 border-white shadow-lg flex items-center justify-center text-2xl font-black text-[#6161FF]">{initials(emp.full_name)}</span>
                )}
                {toggles.photo !== false && (
                  <button data-testid="upload-photo-btn" onClick={() => fileRef.current?.click()}
                    className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-[#181B34] text-white flex items-center justify-center shadow hover:bg-[#6161FF]">
                    <Camera size={14} />
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
              </div>
              <div className="pb-1">
                <h1 className="text-2xl font-black tracking-tight">{emp.full_name}</h1>
                <p className="font-semibold text-[#676a82]">{emp.designation} · {emp.department}</p>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-[#F0F3FF] overflow-hidden">
                <div className="h-full rounded-full bg-[#00C875]" style={{ width: `${completeness}%` }} />
              </div>
              <span className="text-xs font-black text-[#00925a]" data-testid="profile-completeness">{completeness}% complete</span>
            </div>
            <div className="mt-7 space-y-4">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9699AD]">Your self-service fields</p>
              {SS_FIELDS.filter(([k]) => toggles[k] !== false).map(([k, label, ph]) => (
                <div key={k}>
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">{label}</label>
                  <input data-testid={`profile-${k}-input`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                    className="mt-1 w-full h-11 px-3 rounded-xl border border-[#181B34]/15 font-semibold focus:outline-none focus:border-[#6161FF]" />
                </div>
              ))}
              <button data-testid="save-profile-btn" onClick={save} className="w-full h-12 rounded-xl bg-[#6161FF] hover:bg-[#5050E5] text-white font-bold">Save profile</button>
              <p className="text-xs text-[#9699AD] font-medium text-center">Core fields (name, designation, manager…) are managed by HR. Date of birth & address never appear on chart cards.</p>
            </div>
          </div>
        </div>
      </div>
      {showWizard && (
        <Wizard emp={emp} toggles={toggles} onClose={() => { setShowWizard(false); setUser({ ...user, first_login: false }); }} onSaved={load} />
      )}
    </div>
  );
}
